<template>
  <div class="groupbuy-order">
    <el-card class="main-card">
      <div class="header-section">
        <div class="header-left">
          <el-button icon="el-icon-arrow-left" size="small" @click="goBack">返回</el-button>
          <h2 class="page-title">
            <i class="el-icon-s-order title-icon"></i>
            团购订单管理
          </h2>
        </div>
        <div class="groupbuy-info" v-if="groupbuyInfo">
          <span class="info-label">团购：</span>
          <span class="info-value">{{ groupbuyInfo.title }}</span>
          <el-divider direction="vertical"></el-divider>
          <span class="info-label">商品：</span>
          <span class="info-value">{{ groupbuyInfo.goods_name }}</span>
          <el-divider direction="vertical"></el-divider>
          <span class="info-label">团长：</span>
          <span class="info-value">{{ groupbuyInfo.leader_name || '未指定' }}</span>
        </div>
      </div>

      <div class="content-section">
        <!-- 搜索栏 -->
        <div class="search-box">
          <el-row type="flex" :gutter="12" class="search-row">
            <el-col :span="6">
              <el-row type="flex" :gutter="12">
                <el-col :span="24">
                  <el-input
                    size="small"
                    clearable
                    placeholder="请输入订单编号"
                    prefix-icon="el-icon-document"
                    v-model="query.order_no"
                    @keyup.enter.native="getList"
                    class="search-input"
                  />
                </el-col>
              </el-row>
              <el-row type="flex" :gutter="12" style="margin-top: 12px;">
                <el-col :span="24">
                  <el-input
                    size="small"
                    clearable
                    placeholder="请输入收货人姓名"
                    prefix-icon="el-icon-user"
                    v-model="query.consignee_name"
                    @keyup.enter.native="getList"
                    class="search-input"
                  />
                </el-col>
              </el-row>
              <el-row type="flex" :gutter="12" style="margin-top: 12px;">
                <el-col :span="24">
                  <el-input
                    size="small"
                    clearable
                    placeholder="请输入联系电话"
                    prefix-icon="el-icon-phone"
                    v-model="query.consignee_phone"
                    @keyup.enter.native="getList"
                    class="search-input"
                  />
                </el-col>
              </el-row>
            </el-col>
            <el-col :span="18">
              <div class="search-buttons search-buttons-right">
                <el-button type="primary" size="small" @click="getList">查询</el-button>
                <el-button size="small" @click="resetQuery">重置</el-button>
              </div>
            </el-col>
          </el-row>

          <!-- 状态筛选 -->
          <div class="filter-section">
            <span class="filter-label">订单状态：</span>
            <el-radio-group v-model="query.status" size="small" @change="handleStatusChange" class="filter-radio">
              <el-radio-button label="">全部</el-radio-button>
              <el-radio-button label="0">待付款</el-radio-button>
              <el-radio-button label="1">待发货</el-radio-button>
              <el-radio-button label="2">待收货</el-radio-button>
              <el-radio-button label="3">已完成</el-radio-button>
              <el-radio-button label="4">已取消</el-radio-button>
            </el-radio-group>
          </div>
        </div>

        <!-- 订单列表 -->
        <div class="table-wrapper">
          <div class="table-container" v-loading="tableLoading">
            <div v-for="order in orderList" :key="order.id" class="order-card">
              <div class="order-card-header">
                <div class="order-info-left">
                  <span class="order-no-label">订单编号：</span>
                  <span class="order-no-value">{{ order.order_no }}</span>
                </div>
                <div class="order-info-right">
                  <span class="order-status-badge">
                    <el-tag v-if="order.display_status === 0" size="small" type="warning">待付款</el-tag>
                    <el-tag v-else-if="order.display_status === 1" size="small" type="primary">待发货</el-tag>
                    <el-tag v-else-if="order.display_status === 2" size="small" type="success">待收货</el-tag>
                    <el-tag v-else-if="order.display_status === 3" size="small">已完成</el-tag>
                    <el-tag v-else-if="order.display_status === 4" size="small" type="info">已取消</el-tag>
                  </span>
                  <span class="create-time">{{ formatDate(order.create_time) }}</span>
                </div>
              </div>

              <div class="order-card-body">
                <!-- 收货信息 -->
                <div class="address-section">
                  <i class="el-icon-location-outline address-icon"></i>
                  <span class="address-info">
                    <span class="consignee-name">{{ getConsigneeName(order) }}</span>
                    <span class="consignee-phone">{{ getConsigneePhone(order) }}</span>
                    <span class="address-detail">{{ getAddressDetail(order) }}</span>
                  </span>
                </div>

                <!-- 团购商品信息 -->
                <div class="shop-item">
                  <div class="shop-header">
                    <i class="el-icon-goods shop-icon"></i>
                    <span class="shop-name">团购商品</span>
                  </div>
                  <el-table :data="[order.goods]" style="width: 100%" size="small" :show-header="false" class="goods-table">
                    <el-table-column width="80" align="center">
                      <template v-slot="scope">
                        <img
                          v-if="scope.row.image"
                          :src="scope.row.image"
                          class="goods-image"
                        />
                        <div v-else class="no-image">
                          <i class="el-icon-picture"></i>
                        </div>
                      </template>
                    </el-table-column>
                    <el-table-column prop="goods_name" label="商品名称" min-width="200">
                      <template v-slot="scope">
                        <div class="goods-name-cell">{{ scope.row.goods_name }}</div>
                      </template>
                    </el-table-column>
                    <el-table-column prop="price" label="单价" width="100">
                      <template v-slot="scope">
                        <span class="price-cell">¥{{ scope.row.price }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column label="数量" width="80" align="center">
                      <template v-slot="scope">
                        <span class="quantity-cell">× {{ scope.row.quantity }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column label="小计" width="100">
                      <template v-slot="scope">
                        <span class="subtotal-cell">¥{{ (scope.row.price * scope.row.quantity).toFixed(2) }}</span>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>

              <div class="order-card-footer">
                <div class="order-summary">
                  <span class="summary-text">
                    <i class="el-icon-goods"></i>
                    共 <span class="highlight-text">{{ order.goods.quantity }}</span> 件商品
                  </span>
                  <span class="total-amount">
                    订单合计：
                    <span class="amount-text">¥{{ order.total_amount }}</span>
                  </span>
                </div>
                <div class="order-actions">
                  <el-button
                    size="small"
                    icon="el-icon-view"
                    class="action-btn view-btn"
                    @click="openDetail(order)"
                  >
                    详情
                  </el-button>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-if="orderList.length === 0 && !tableLoading" class="empty-state">
              <i class="el-icon-s-order empty-icon"></i>
              <p class="empty-text">暂无订单数据</p>
            </div>

            <!-- 分页 -->
            <el-pagination
              class="pagination-box"
              :pager-count="7"
              @current-change="pageChange"
              @size-change="sizeChange"
              :page-sizes="[30, 60, 100]"
              layout="total, sizes, prev, pager, next, jumper"
              :page-size="query.limit"
              background
              :total="exp.total"
            />
          </div>
        </div>
      </div>
    </el-card>

    <!-- 订单详情弹窗 -->
    <el-dialog
      v-dialogDrag
      title="订单详情"
      :visible.sync="detailVisible"
      width="800px"
      class="detail-dialog"
    >
      <div v-if="detailInfo" class="detail-content">
        <el-descriptions :column="2" border size="small" class="detail-table">
          <el-descriptions-item label="订单编号">{{ detailInfo.order_no }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">
            <el-tag v-if="detailInfo.display_status === 0" size="small" type="warning">待付款</el-tag>
            <el-tag v-else-if="detailInfo.display_status === 1" size="small" type="primary">待发货</el-tag>
            <el-tag v-else-if="detailInfo.display_status === 2" size="small" type="success">待收货</el-tag>
            <el-tag v-else-if="detailInfo.display_status === 3" size="small">已完成</el-tag>
            <el-tag v-else-if="detailInfo.display_status === 4" size="small" type="info">已取消</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="收货人">{{ getConsigneeName(detailInfo) }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ getConsigneePhone(detailInfo) }}</el-descriptions-item>
          <el-descriptions-item label="收货地址" :span="2">{{ getAddressDetail(detailInfo) }}</el-descriptions-item>
          <el-descriptions-item label="下单时间">{{ formatDate(detailInfo.create_time) }}</el-descriptions-item>
          <el-descriptions-item label="发货时间" v-if="detailInfo.ship_time">{{ formatDate(detailInfo.ship_time) }}</el-descriptions-item>
        </el-descriptions>

        <div v-if="detailInfo.goods" class="goods-detail-section">
          <h3 class="section-title">
            <i class="el-icon-goods"></i>
            商品信息
          </h3>
          <el-descriptions :column="1" border size="small">
            <el-descriptions-item label="商品名称">{{ detailInfo.goods.goods_name }}</el-descriptions-item>
            <el-descriptions-item label="单价">¥{{ detailInfo.goods.price }}</el-descriptions-item>
            <el-descriptions-item label="数量">{{ detailInfo.goods.quantity }}</el-descriptions-item>
            <el-descriptions-item label="小计">¥{{ (detailInfo.goods.price * detailInfo.goods.quantity).toFixed(2) }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 物流轨迹 -->
        <div v-if="detailInfo.logistics && detailInfo.logistics.traces && detailInfo.logistics.traces.length > 0" class="logistics-section">
          <h3 class="section-title">
            <i class="el-icon-truck"></i>
            物流轨迹
          </h3>
          <div class="logistics-timeline">
            <div v-for="(item, index) in detailInfo.logistics.traces" :key="index" class="logistics-item">
              <div class="logistics-dot" :class="{ 'is-first': index === 0 }"></div>
              <div class="logistics-content">
                <div class="logistics-time">{{ formatLogisticsTime(item.op_time) }}</div>
                <div class="logistics-desc">{{ item.desc }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getGroupBuyOrderList, getGroupBuyOrderDetail, getGroupBuyDetail } from '@/api/zhiqing/leifeng_groupbuy'

export default {
  name: 'LeifengGroupbuyOrderList',
  data() {
    return {
      tableLoading: false,
      exp: { total: 0 },
      groupbuy_id: '',
      groupbuyInfo: null,
      query: {
        page: 1,
        limit: 20,
        order_no: '',
        consignee_name: '',
        consignee_phone: '',
        status: '',
      },
      orderList: [],
      detailVisible: false,
      detailInfo: null,
    }
  },
  created() {
    this.groupbuy_id = this.$route.params.groupbuy_id
    this.loadGroupbuyInfo()
    this.getList()
  },
  methods: {
    // 加载团购活动信息（优先从 sessionStorage，回退到接口）
    loadGroupbuyInfo() {
      const info = window.sessionStorage.getItem('groupbuy_order_info')
      if (info) {
        try {
          this.groupbuyInfo = JSON.parse(info)
        } catch (e) {
          this.groupbuyInfo = null
        }
      }
      // sessionStorage 没有则调接口获取
      if (!this.groupbuyInfo && this.groupbuy_id) {
        getGroupBuyDetail({ id: this.groupbuy_id }).then(res => {
          if (res && res.data && res.data.code == 200 && res.data.data) {
            this.groupbuyInfo = res.data.data
          }
        }).catch(() => {})
      }
    },
    getList() {
      this.tableLoading = true
      const params = {
        groupbuy_id: this.groupbuy_id,
        order_no: this.query.order_no,
        consignee_name: this.query.consignee_name,
        consignee_phone: this.query.consignee_phone,
        status: this.query.status === '' ? -1 : this.query.status,
        page_code: this.query.page,
        page_num: this.query.limit,
      }
      getGroupBuyOrderList(params).then(res => {
        // 防御性检查：响应可能是 HTML 或非预期结构
        if (res && res.data && res.data.code == 200 && res.data.data) {
          const d = res.data.data
          this.orderList = d.data || []
          this.exp.total = d.total || 0
        } else {
          this.orderList = []
          this.exp.total = 0
          if (res && res.data && res.data.msg) {
            this.$message.error(res.data.msg)
          }
        }
      }).catch(() => {
        this.orderList = []
        this.exp.total = 0
      }).finally(() => {
        this.tableLoading = false
      })
    },
    pageChange(page) {
      this.query.page = page
      this.getList()
    },
    sizeChange(size) {
      this.query.limit = size
      this.query.page = 1
      this.getList()
    },
    handleStatusChange() {
      this.query.page = 1
      this.getList()
    },
    resetQuery() {
      this.query = {
        page: 1,
        limit: 20,
        order_no: '',
        consignee_name: '',
        consignee_phone: '',
        status: '',
      }
      this.getList()
    },
    goBack() {
      this.$router.push('/leifeng/groupbuy')
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0') + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0') + ':' + String(date.getSeconds()).padStart(2, '0')
    },
    getConsigneeName(order) {
      return order.receiver_name || order.consignee_name || ''
    },
    getConsigneePhone(order) {
      return order.receiver_phone || order.consignee_phone || ''
    },
    getAddressDetail(order) {
      const parts = []
      if (order.province) parts.push(order.province)
      if (order.city) parts.push(order.city)
      if (order.district) parts.push(order.district)
      if (order.detail_address) parts.push(order.detail_address)
      return parts.join('')
    },
    formatLogisticsTime(timestamp) {
      if (!timestamp) return ''
      if (typeof timestamp === 'string') return timestamp
      const date = new Date(timestamp * 1000)
      return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0') + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
    },
    openDetail(order) {
      getGroupBuyOrderDetail({ id: order.id }).then(res => {
        if (res && res.data && res.data.code == 200 && res.data.data) {
          this.detailInfo = res.data.data
          this.detailVisible = true
        } else {
          // 接口失败时回退到列表行数据
          this.detailInfo = { ...order, logistics: null }
          this.detailVisible = true
          if (res && res.data && res.data.msg) {
            this.$message.error(res.data.msg)
          }
        }
      }).catch(() => {
        this.detailInfo = { ...order, logistics: null }
        this.detailVisible = true
      })
    },
  },
}
</script>

<style scoped>
.groupbuy-order {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 120px);
}

.main-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08);
  border: none;
}

.header-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f2f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
  color: #409eff;
}

.groupbuy-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 14px;
}

.info-label {
  color: #909399;
}

.info-value {
  color: #303133;
  font-weight: 500;
}

.search-box {
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e4e7ed;
}

.search-row {
  margin-bottom: 16px;
}

.search-input:hover {
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.search-buttons-right {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  align-items: center;
}

.search-buttons-right .el-button {
  margin: 0;
  height: 28px;
  line-height: 26px;
  padding: 0 10px;
  font-size: 12px;
  min-width: auto;
  width: auto;
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.filter-radio {
  flex: 1;
}

.table-wrapper {
  background: #fff;
  border-radius: 8px;
}

.table-container {
  padding: 10px 0;
}

.order-card {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  margin-bottom: 20px;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.order-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.order-card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ebeef5;
}

.order-info-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-no-label {
  font-size: 14px;
  color: #909399;
}

.order-no-value {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.order-info-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.create-time {
  font-size: 13px;
  color: #909399;
}

.order-card-body {
  padding: 16px 20px;
  background: #fafbfc;
}

.address-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
  border-radius: 6px;
  border-left: 4px solid #1890ff;
}

.address-icon {
  font-size: 20px;
  color: #1890ff;
  margin-top: 2px;
}

.address-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.consignee-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.consignee-phone {
  font-size: 14px;
  color: #606266;
  margin-left: 12px;
}

.address-detail {
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
}

.shop-item {
  margin-bottom: 16px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.shop-item:last-child {
  margin-bottom: 0;
}

.shop-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border-bottom: 1px solid #ebeef5;
}

.shop-icon {
  font-size: 18px;
  color: #409eff;
}

.shop-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.goods-table {
  border-radius: 0;
  overflow: hidden;
}

.goods-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.no-image {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  border-radius: 6px;
  border: 1px dashed #dcdfe6;
}

.no-image i {
  font-size: 24px;
  color: #c0c4cc;
}

.goods-name-cell {
  font-weight: 500;
  color: #303133;
}

.price-cell {
  color: #606266;
  font-weight: 500;
}

.quantity-cell {
  color: #909399;
}

.subtotal-cell {
  color: #409eff;
  font-weight: 600;
}

.order-card-footer {
  padding: 16px 20px;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #ebeef5;
}

.order-summary {
  display: flex;
  align-items: center;
  gap: 24px;
}

.summary-text {
  font-size: 14px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 6px;
}

.highlight-text {
  color: #409eff;
  font-weight: 600;
  font-size: 16px;
}

.total-amount {
  font-size: 14px;
  color: #606266;
}

.amount-text {
  font-size: 20px;
  font-weight: 700;
  color: #f56c6c;
}

.order-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.action-btn {
  transition: all 0.3s;
  border-radius: 4px;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.primary-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.primary-btn:hover {
  background: linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%);
}

.view-btn {
  background: #ecf5ff;
  color: #409eff;
  border-color: #b3d8ff;
}

.view-btn:hover {
  background: #409eff;
  color: #fff;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  color: #dcdfe6;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

.pagination-box {
  margin-top: 24px;
  text-align: center;
  padding: 10px 0;
}

.goods-detail-section {
  margin-top: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: #409eff;
}

.dialog-footer {
  text-align: center;
}

/* 物流轨迹样式 */
.logistics-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.logistics-timeline {
  position: relative;
  padding-left: 24px;
  max-height: 400px;
  overflow-y: auto;
}

.logistics-item {
  position: relative;
  padding-bottom: 20px;
  display: flex;
  gap: 16px;
}

.logistics-item:last-child {
  padding-bottom: 0;
}

.logistics-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #dcdfe6;
  border: 2px solid #fff;
  flex-shrink: 0;
  margin-top: 4px;
  position: relative;
  z-index: 1;
  box-shadow: 0 0 0 2px #e4e7ed;
}

.logistics-dot.is-first {
  width: 14px;
  height: 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
}

.logistics-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 20px;
  bottom: 0;
  width: 2px;
  background: #e4e7ed;
}

.logistics-content {
  flex: 1;
}

.logistics-time {
  font-size: 13px;
  color: #909399;
  margin-bottom: 4px;
}

.logistics-desc {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.logistics-item:first-child .logistics-desc {
  color: #303133;
  font-weight: 500;
}

.order-checkbox {
  margin-right: 12px;
}

.batch-ship-info {
  margin-bottom: 20px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  line-height: 1.6;
}

@media screen and (max-width: 768px) {
  .groupbuy-order {
    padding: 12px;
  }
  .order-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .order-info-right {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .order-card-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .order-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .filter-section {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
